import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, CheckCircle2, Lock, Key, Trash2, Users, Unlock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import AppHeader from "@/components/AppHeader";
import EmptyState from "@/components/EmptyState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [pendingHasMore, setPendingHasMore] = useState(false);
  const [verifiedProducts, setVerifiedProducts] = useState([]);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [correctionsCount, setCorrectionsCount] = useState(0);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '' });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (!response.data.is_admin) {
          toast.error('Access denied');
          navigate('/dashboard');
          return;
        }
        setUser(response.data);
        await fetchProducts();
        await fetchCorrections();
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate('/');
        } else {
          toast.error('Failed to load the admin panel. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchProducts = async () => {
    try {
      // Pending: paged; "has more" derived from a full page being returned.
      const pendingResponse = await axios.get(`${API}/admin/products/pending?limit=${PAGE_SIZE}&skip=0`);
      setProducts(pendingResponse.data);
      setPendingHasMore(pendingResponse.data.length === PAGE_SIZE);

      // Verified: filtered server-side (the old client-side filter over the
      // first /products page silently hid verified products beyond page 1).
      const [verifiedResponse, countResponse] = await Promise.all([
        axios.get(`${API}/products?locked=true&limit=${PAGE_SIZE}&skip=0`),
        axios.get(`${API}/products/count?locked=true`),
      ]);
      setVerifiedProducts(verifiedResponse.data);
      setVerifiedCount(countResponse.data.count);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    }
  };

  const fetchCorrections = async () => {
    try {
      const [list, count] = await Promise.all([
        axios.get(`${API}/admin/corrections?status=open&limit=${PAGE_SIZE}`),
        axios.get(`${API}/admin/corrections/count?status=open`),
      ]);
      setCorrections(list.data);
      setCorrectionsCount(count.data.count);
    } catch (error) {
      console.error('Error fetching corrections:', error);
    }
  };

  const resolveCorrection = async (correctionId) => {
    try {
      await axios.patch(`${API}/admin/corrections/${correctionId}/resolve`);
      toast.success('Marked as resolved');
      await fetchCorrections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve');
    }
  };

  const loadMorePending = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/admin/products/pending?limit=${PAGE_SIZE}&skip=${products.length}`);
      setProducts((prev) => [...prev, ...data]);
      setPendingHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      toast.error('Could not load more products. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreVerified = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/products?locked=true&limit=${PAGE_SIZE}&skip=${verifiedProducts.length}`);
      setVerifiedProducts((prev) => [...prev, ...data]);
    } catch (error) {
      toast.error('Could not load more products. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLockProduct = async (productId) => {
    try {
      await axios.patch(`${API}/admin/products/${productId}/lock`);
      toast.success('Product locked successfully');
      await fetchProducts();
    } catch (error) {
      console.error('Lock error:', error);
      toast.error('Failed to lock product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted successfully');
      await fetchProducts();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    try {
      await axios.patch(`${API}/auth/admin/change-password`, passwordData);
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '' });
      setDialogOpen(false);
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AppHeader
        right={
          <>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/users')} data-testid="user-management-button">
            <Users className="w-4 h-4 mr-2" />
            User Management
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="change-password-trigger">
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Admin Password</DialogTitle>
                <DialogDescription>
                  Update your admin password here.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <Button type="submit" data-testid="submit-password-change">Change Password</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Button size="sm" variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          </>
        }
      />

      <div className="dashboard-content">
        <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '2rem' }} data-testid="admin-panel-title">
          Product Management
        </h1>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-3" style={{ marginBottom: '2rem' }}>
            <TabsTrigger value="pending">Pending Review ({products.length})</TabsTrigger>
            <TabsTrigger value="verified">Verified ({verifiedCount.toLocaleString()})</TabsTrigger>
            <TabsTrigger value="corrections" data-testid="corrections-tab">
              Corrections {correctionsCount > 0 && `(${correctionsCount})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {products.length === 0 ? (
              <EmptyState icon={CheckCircle2} title="All caught up" hint="No products waiting for review." testId="empty-pending" />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
                {products.map((product) => (
                  <Card key={product.id} data-testid={`admin-product-${product.id}`}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <CardTitle>{product.name}</CardTitle>
                          <CardDescription>Barcode: {product.barcode}</CardDescription>
                        </div>
                        <Badge
                          variant={product.status === 'safe' ? 'success' : 'destructive'}
                          style={{
                            background: product.status === 'safe' ? '#e8f5e9' : '#ffebee',
                            color: product.status === 'safe' ? '#2e7d32' : '#c62828'
                          }}
                        >
                          {product.status === 'safe' ? '✓ Safe' : '⚠ Contains Cilantro'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {product.image_url && (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem' }}
                        />
                      )}
                      <div style={{ marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))' }}>Reviews: {product.reviews?.length || 0}</p>
                        {product.reviews && product.reviews.length > 0 && (
                          <div style={{ marginTop: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
                            {product.reviews.map((rev, idx) => (
                              <div key={idx} style={{ padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                                <div style={{ fontWeight: 600 }}>{rev.user_name}</div>
                                <div style={{ color: 'hsl(var(--muted-foreground))' }}>{rev.comment}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        className="w-full"
                        onClick={() => handleLockProduct(product.id)}
                        data-testid={`lock-product-${product.id}`}
                        style={{ marginBottom: '0.5rem' }}
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Lock Status (Verify)
                      </Button>
                      <Button
                        variant="destructive"
                        className="w-full"
                        onClick={() => handleDeleteProduct(product.id)}
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Product
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {pendingHasMore && (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <Button variant="outline" onClick={loadMorePending} disabled={loadingMore} data-testid="load-more-pending">
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="verified">
            {verifiedProducts.length === 0 ? (
              <EmptyState icon={Lock} title="No verified products yet" hint="Lock a pending product to verify it for the community." testId="empty-verified" />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
                {verifiedProducts.map((product) => (
                  <Card key={product.id} data-testid={`verified-product-${product.id}`}>
                    <CardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <CardTitle>{product.name}</CardTitle>
                          <CardDescription>Barcode: {product.barcode}</CardDescription>
                        </div>
                        <Badge
                          variant={product.status === 'safe' ? 'success' : 'destructive'}
                          style={{
                            background: product.status === 'safe' ? '#e8f5e9' : '#ffebee',
                            color: product.status === 'safe' ? '#2e7d32' : '#c62828'
                          }}
                        >
                          {product.status === 'safe' ? '✓ Safe' : '⚠ Contains Cilantro'}
                        </Badge>
                      </div>
                      <Badge variant="outline" style={{ marginTop: '0.5rem', width: 'fit-content' }}>
                        🔒 Verified
                      </Badge>
                    </CardHeader>
                    <CardContent>
                      {product.image_url && (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem' }}
                        />
                      )}
                      <div style={{ marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))' }}>Reviews: {product.reviews?.length || 0}</p>
                      </div>
                      <Button
                        variant="destructive"
                        className="w-full"
                        onClick={() => handleDeleteProduct(product.id)}
                        data-testid={`delete-verified-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Product
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {verifiedProducts.length > 0 && verifiedProducts.length < verifiedCount && (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <Button variant="outline" onClick={loadMoreVerified} disabled={loadingMore} data-testid="load-more-verified">
                  {loadingMore ? 'Loading…' : `Load more (${verifiedProducts.length} of ${verifiedCount.toLocaleString()})`}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="corrections">
            {corrections.length === 0 ? (
              <EmptyState icon={CheckCircle2} title="No open correction requests" hint="User-reported data issues will appear here." testId="empty-corrections" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {corrections.map((c) => (
                  <Card key={c.id} data-testid={`correction-${c.id}`}>
                    <CardContent style={{ padding: '1.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ flex: 1, minWidth: '250px' }}>
                          <div style={{ fontWeight: 600 }}>
                            {c.product_name}
                            <span style={{ fontWeight: 400, fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginLeft: '0.5rem' }}>
                              {c.barcode}
                            </span>
                          </div>
                          <p style={{ marginTop: '0.5rem', fontSize: '0.95rem', lineHeight: 1.5 }}>{c.message}</p>
                          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                            by {c.user_name} · {new Date(c.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/product/${c.barcode}`)} data-testid={`open-product-${c.id}`}>
                            Open product
                          </Button>
                          <Button size="sm" onClick={() => resolveCorrection(c.id)} data-testid={`resolve-${c.id}`}>
                            Mark resolved
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;